// Documenti HSE azienda: DVR, POS, DUVRI, DURC, ecc.
import { useRef, useState } from 'react'
import { Upload, Download, Send, Plus } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { Table } from '../../components/ui/Table'
import { formatDate } from '../../lib/utils'
import {
  useSafetyDocuments,
  useUploadDocument,
  useDownloadDocument,
  type SafetyDocument,
} from '../../hooks/useHse'
import { SendDocumentsModal, type DocToSend } from './components/SendDocumentsModal'
import { NewDocumentModal } from './components/NewDocumentModal'

function DocRow({ doc, onSendToggle, isSelected }: {
  doc: SafetyDocument
  onSendToggle: (d: DocToSend, checked: boolean) => void
  isSelected: boolean
}) {
  const uploadMutation = useUploadDocument(doc.id)
  const downloadMutation = useDownloadDocument()
  const fileRef = useRef<HTMLInputElement>(null)

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    uploadMutation.mutate(file)
    e.target.value = ''
  }

  const handleDownload = async () => {
    const result = await downloadMutation.mutateAsync(doc.id)
    window.open(result.url, '_blank')
  }

  const hasFile = Boolean(doc.filePath)

  return (
    <div className="flex items-center gap-2">
      {hasFile && (
        <input
          type="checkbox"
          className="h-4 w-4 rounded border-[var(--fc-border)] accent-[var(--fc-primary)]"
          checked={isSelected}
          onChange={(e) =>
            onSendToggle(
              { type: 'safety_doc', id: doc.id, fileName: doc.fileName ?? '', label: `${doc.type} — ${doc.title}` },
              e.target.checked,
            )
          }
        />
      )}
      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleUpload} />
      {!hasFile ? (
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploadMutation.isPending}
          className="flex items-center gap-1 text-xs text-[var(--fc-primary)] hover:underline disabled:opacity-50"
        >
          <Upload className="h-3 w-3" />
          {uploadMutation.isPending ? 'Caricamento...' : 'Carica'}
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="text-xs text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]"
            title="Sostituisci file"
          >
            <Upload className="h-3 w-3" />
          </button>
          <button
            onClick={handleDownload}
            disabled={downloadMutation.isPending}
            className="flex items-center gap-1 text-xs text-[var(--fc-primary)] hover:underline disabled:opacity-50"
            title={doc.fileName}
          >
            <Download className="h-3 w-3" />
            {doc.fileName ? doc.fileName.slice(0, 18) + (doc.fileName.length > 18 ? '…' : '') : 'Scarica'}
          </button>
        </div>
      )}
    </div>
  )
}

export function DocumentsPage() {
  const [selectedDocs, setSelectedDocs] = useState<DocToSend[]>([])
  const [showSendModal, setShowSendModal] = useState(false)
  const [showNewModal, setShowNewModal] = useState(false)

  const { data = [], isLoading } = useSafetyDocuments()

  const toggleDoc = (doc: DocToSend, checked: boolean) => {
    if (checked) {
      setSelectedDocs((prev) => [...prev.filter((d) => d.id !== doc.id), doc])
    } else {
      setSelectedDocs((prev) => prev.filter((d) => d.id !== doc.id))
    }
  }

  const columns = [
    {
      key: 'type',
      header: 'Tipo',
      cell: (d: SafetyDocument) => <Badge variant="default">{d.type ?? '—'}</Badge>,
      className: 'w-24',
    },
    {
      key: 'title',
      header: 'Documento',
      cell: (d: SafetyDocument) => <span className="font-medium">{d.title ?? '—'}</span>,
    },
    {
      key: 'validFrom',
      header: 'Dal',
      cell: (d: SafetyDocument) => (
        <span className="text-sm text-[var(--fc-foreground-muted)]">
          {d.issueDate ? formatDate(d.issueDate, 'dd/MM/yyyy') : '—'}
        </span>
      ),
      className: 'w-28',
    },
    {
      key: 'validTo',
      header: 'Al',
      cell: (d: SafetyDocument) => (
        <span className="text-sm text-[var(--fc-foreground-muted)]">
          {d.expiryDate ? formatDate(d.expiryDate, 'dd/MM/yyyy') : '—'}
        </span>
      ),
      className: 'w-28',
    },
    {
      key: 'status',
      header: 'Stato',
      cell: (d: SafetyDocument) => {
        const now = new Date()
        const expiry = d.expiryDate ? new Date(d.expiryDate) : null
        const soon = expiry ? new Date(expiry.getTime() - 30 * 86400000) : null
        const st = !expiry ? 'valid' : expiry < now ? 'expired' : (soon && soon < now) ? 'expiring' : 'valid'
        return (
          <Badge variant={st === 'valid' ? 'success' : st === 'expiring' ? 'warning' : 'danger'}>
            {st === 'valid' ? 'Valido' : st === 'expiring' ? 'In scadenza' : 'Scaduto'}
          </Badge>
        )
      },
      className: 'w-28',
    },
    {
      key: 'file',
      header: 'File',
      cell: (d: SafetyDocument) => (
        <DocRow
          doc={d}
          isSelected={selectedDocs.some((s) => s.id === d.id)}
          onSendToggle={toggleDoc}
        />
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="Documenti HSE"
        breadcrumb={[{ label: 'HSE', href: '/hse' }, { label: 'Documenti' }]}
        actions={
          <div className="flex gap-2">
            {selectedDocs.length > 0 && (
              <Button
                leftIcon={<Send className="h-4 w-4" />}
                onClick={() => setShowSendModal(true)}
              >
                Invia selezionati ({selectedDocs.length})
              </Button>
            )}
            <Button variant="secondary" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowNewModal(true)}>
              Nuovo documento
            </Button>
          </div>
        }
      />
      <Table
        data={data}
        columns={columns}
        keyExtractor={(d) => d.id}
        isLoading={isLoading}
        emptyMessage="Nessun documento caricato"
      />

      <SendDocumentsModal
        isOpen={showSendModal}
        onClose={() => { setShowSendModal(false); setSelectedDocs([]) }}
        preselected={selectedDocs}
      />

      <NewDocumentModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
      />
    </div>
  )
}
